import { Component, OnInit, Pipe, PipeTransform } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BarcodeSetting, FieldType, FileConfiguration, LabelPrintingData, LabelsNeededSummary, LabelType, PresetSetting, Sortable } from './label-printing.model';
import { JhiLanguageService } from '../shared/core-utils';
import { LabelPrintingContext } from './label-printing.context';
import { LabelPrintingService } from './label-printing.service';
import { FileDownloadHelper } from '../entities/sample/file-download.helper';
import { HttpErrorResponse } from '@angular/common/http';
import { AlertService } from '../shared/alert/alert.service';
import { HelpService } from '../shared/service/help.service';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalConfirmComponent } from '../shared/modal/modal-confirm.component';
import { ParamContext } from '../shared/service/param.context';
import { finalize } from 'rxjs/internal/operators/finalize';
import {
    HELP_LABEL_PRINTING_GERMPLASM_LIST_MANAGER, HELP_LABEL_PRINTING_GERMPLASM_MANAGER,
    HELP_LABEL_PRINTING_INVENTORY_MANAGER, HELP_LABEL_PRINTING_SAMPLE_MANAGER, HELP_LABEL_PRINTING_STUDY_ENTRIES_MANAGER, HELP_LABEL_PRINTING_STUDY_MANAGER, ViewEnum
} from '../app.constants';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { UrlService } from '../shared/service/url.service';
import { StudyEditorService } from '../study-editor/study-editor.service';
import { Program } from '../shared/program/model/program';
import { TranslateService } from '@ngx-translate/core';

export enum FileType {
    NONE = '',
    CSV = 'csv',
    PDF = 'pdf',
    EXCEL = 'xlsx'
}

/** aka printingLabelType in params */
export enum LabelPrintingType {
    OBSERVATION_DATASET = 'ObservationDataset',
    SUBOBSERVATION_DATASET = 'SubObservationDataset',
    LOT = 'Lot',
    GERMPLASM = 'Germplasm',
    GERMPLASM_LIST = 'Germplasm List',
    STUDY_ENTRIES = 'Study Entries',
    SAMPLE = 'Sample'
}

const typesWithHeaderDetails: LabelPrintingType[] = [
    LabelPrintingType.OBSERVATION_DATASET, LabelPrintingType.SUBOBSERVATION_DATASET
];


declare const $: any;
const MAX_LABELS_PER_SIDE_FOR_PDF_FORMAT = 5;
const SELECTED_FIELDS = 'Selected Fields';
const LEFT_SIDE_FIELDS = 'Left Side Fields';
const RIGHT_SIDE_FIELDS = 'Right Side Fields';
@Component({
    selector: 'jhi-label-printing',
    templateUrl: './label-printing.component.html',
    styleUrls: ['./label-printing.component.css']
})
export class LabelPrintingComponent implements OnInit {
    initComplete: boolean;

    labelPrintingData: LabelPrintingData = new LabelPrintingData();
    labelsNeededSummary: LabelsNeededSummary;
    metadata: Map<string, string>;
    metadataKeys: string[];
    labelTypes: LabelType[];
    FILE_TYPES = FileType;
    fileType: FileType = FileType.NONE;
    selectedfileType: FileType = FileType.NONE;
    presetSettingId: number;
    loadSavedSettings = false;
    fieldsSelected: LabelType[];
    presetSettings: PresetSetting[];
    modalTitle: string;
    modalMessage: string;
    helpLink: string;

    sortableFields: Sortable[];
    sortBySelected: any = '';
    isLoading: boolean;
    isPresetSaving: boolean;
    defaultPresetSetting: PresetSetting;
    collapsedMap: { [key: string]: boolean; } = {};
    labelTypesOrigMap: { [key: string]: {id: number, name: string, fieldType: FieldType }[]; } = {};
    selectedFilterTextMap: { [key: string]: string; } = {};
    allLabels: any[];

    constructor(private route: ActivatedRoute,
                private router: Router,
                private context: LabelPrintingContext,
                private service: LabelPrintingService,
                private languageService: JhiLanguageService,
                private fileDownloadHelper: FileDownloadHelper,
                private alertService: AlertService,
                public activeModal: NgbActiveModal,
                private modalService: NgbModal,
                private paramContext: ParamContext,
                private helpService: HelpService,
                private urlService: UrlService,
                private translateService: TranslateService,
                private studyEditorService: StudyEditorService) {
        this.paramContext.readParams();
    }

    proceed(): void {
    }

    ngOnInit() {
        const params = this.route.snapshot.queryParams;
        this.context.datasetId = params['datasetId'];
        this.context.studyId = params['studyId'];
        this.context.studyName = params['studyName'];
        this.context.printingLabelType = params['printingLabelType'];
        if (this.context.printingLabelType === LabelPrintingType.GERMPLASM) {
            this.context.selectedGIDs = params['selectedGIDs'];
            this.context.isBrowseAllGermplasmResults = params['isBrowseAllGermplasmResults'];
        }
        if (this.context.printingLabelType === LabelPrintingType.LOT) {
            const selectedLotUUIDs = params['selectedLotUUIDs'];
            this.context.selectedLotUUIDs = selectedLotUUIDs || [];
        }
        if (this.context.printingLabelType === LabelPrintingType.SAMPLE) {
            this.context.sampleListId = params['sampleListId'];
            this.context.selectedColumns = params['selectedColumns'];
        }
        this.context.searchRequestId = params['searchRequestId'];
        this.context.listId = params['listId'];
        this.context.openedDirectlyFromNewStudyEditor = params['openedDirectlyFromNewStudyEditor'] === 'true';

        let labelsNeededPromise = Promise.resolve({});
        if (this.hasHeader()) {
            labelsNeededPromise = this.service.getLabelsNeededSummary().toPromise();
            labelsNeededPromise.then((summary: any) => {
                this.labelsNeededSummary = summary;
            });
        }

        const metadataPromise = this.service.getOriginResourceMetadada().toPromise();
        metadataPromise.then((originResourceMetadata) => {
            this.metadata = new Map(Object.entries(originResourceMetadata.metadata));
            this.metadataKeys = Array.from(this.metadata.keys());
            this.labelPrintingData.filename = originResourceMetadata.defaultFileName;
        }).catch(() => {
            this.initComplete = true;
            return;
        });

        const fieldsPromise = this.service.getAvailableLabelFields().toPromise();
        fieldsPromise.then((labelTypes) => {
            this.labelTypes = labelTypes;
            this.labelTypes.forEach((labelType) => {
                this.collapsedMap[labelType.title] = true;
                this.labelTypesOrigMap[labelType.title] = labelType.fields.map((x) => Object.assign({}, x));
            });
            this.allLabels = Object.values(this.labelTypesOrigMap)
                .reduce((allFields, fields) => allFields.concat(fields));
        });

        this.fieldsSelected = [];
        this.fieldsSelected.push(new LabelType('Selected Fields', null, []));
        const presetPromise = this.loadPresets();

        const sorteableFieldsPromise = this.service.getSortableFields().toPromise();
        sorteableFieldsPromise.then((sortables) => {
            this.sortableFields = sortables;
        });

        const defaultSelectionPromise = this.service.getDefaultSettings().toPromise();
        defaultSelectionPromise.then((defaultSelection) => {
            this.defaultPresetSetting = defaultSelection;
        }).catch(() => {
            this.initComplete = true;
            return;
        });

        Promise.all([
            labelsNeededPromise,
            metadataPromise,
            fieldsPromise,
            presetPromise,
            sorteableFieldsPromise,
            defaultSelectionPromise
        ]).then(() => {
            this.initComplete = true;
            if (this.defaultPresetSetting) {
                this.loadPresetSetting(this.defaultPresetSetting);
            }
        });

        this.labelPrintingData.sizeOfLabelSheet = '1';
        this.labelPrintingData.numberOfRowsPerPage = 7;

        // Get helplink url
        if (!this.helpLink || !this.helpLink.length) {

            this.helpService.getHelpLink(this.getHelpLinkString()).toPromise().then((response) => {
                this.helpLink = response.body;
            });
        }
    }

    getHelpLinkString() {
        if (this.context.printingLabelType === LabelPrintingType.GERMPLASM) {
            return HELP_LABEL_PRINTING_GERMPLASM_MANAGER;
        } else if (this.context.printingLabelType === LabelPrintingType.GERMPLASM_LIST) {
            return HELP_LABEL_PRINTING_GERMPLASM_LIST_MANAGER;
        } else if (this.context.printingLabelType === LabelPrintingType.SUBOBSERVATION_DATASET
            || this.context.printingLabelType === LabelPrintingType.OBSERVATION_DATASET) {
            return HELP_LABEL_PRINTING_STUDY_MANAGER;
        } else if (this.context.printingLabelType === LabelPrintingType.LOT) {
            return HELP_LABEL_PRINTING_INVENTORY_MANAGER;
        } else if (this.context.printingLabelType === LabelPrintingType.STUDY_ENTRIES) {
            return HELP_LABEL_PRINTING_STUDY_ENTRIES_MANAGER;
        } else if (this.context.printingLabelType === LabelPrintingType.SAMPLE) {
            return HELP_LABEL_PRINTING_SAMPLE_MANAGER;
        }
    }

    hasHeader() {
        return typesWithHeaderDetails.indexOf(this.context.printingLabelType) !== -1;
    }

    isForGermplasmListLabelPrinting() {
        return this.context.printingLabelType === LabelPrintingType.GERMPLASM_LIST;
    }

    /**
     * Indicates if the export is for label printing
     */
    isLabelPrinting() {
        return this.fileType === FileType.PDF;
    }

    applySelectedSetting() {
        const presetId = Number(this.presetSettingId);
        if (presetId !== 0) {
            this.loadPresetSetting(this.presetSettings.filter((preset) => preset.id === presetId)[0]);
        } else {
            this.labelPrintingData.settingsName = '';
        }
    }

    loadPresetSetting(presetSetting: PresetSetting) {
        this.fileType = this.getFileType(presetSetting.fileConfiguration.outputType);
        this.selectedfileType = this.fileType;
        const labelFieldsSelected: LabelType[] = [];

        this.reloadLabelTypeFields();

        const fieldIndex = new Map<string, any>();
        for (const label of this.labelTypes) {
            for (const field of label.fields) {
                fieldIndex.set(this.transformToCombinedKey(field), field);
            }
        }

        const isPdf = presetSetting.fileConfiguration.outputType === FileType.PDF.toString();

        presetSetting.selectedFields.forEach((idsSelected: string[], idx: number) => {
            const title = isPdf ? (idx === 0 ? LEFT_SIDE_FIELDS : RIGHT_SIDE_FIELDS) : SELECTED_FIELDS;

            const orderedFields: any[] = [];
            for (const id of idsSelected) {
                const field = fieldIndex.get(id);
                if (field) {
                    orderedFields.push({ ...field });
                }
            }

            labelFieldsSelected.push(new LabelType(title, title, orderedFields));
        });

        const usedIds = new Set<string>();
        for (const lt of labelFieldsSelected) {
            for (const field of lt.fields) {
                usedIds.add(this.transformToCombinedKey(field));
            }
        }

        for (const label of this.labelTypes) {
            const kept: any[] = [];
            for (const field of label.fields) {
                if (!usedIds.has(this.transformToCombinedKey(field))) {
                    kept.push({ ...field });
                }
            }
            label.fields = kept;
        }

        this.fieldsSelected = labelFieldsSelected;
        this.sortBySelected = (presetSetting.sortBy) ? presetSetting.sortBy : '';

        if (presetSetting.fileConfiguration.outputType === FileType.PDF.toString()) {
            this.labelPrintingData.numberOfRowsPerPage = presetSetting.fileConfiguration.numberOfRowsPerPage;
            this.labelPrintingData.sizeOfLabelSheet = presetSetting.fileConfiguration.sizeOfLabelSheet;
        }

        this.labelPrintingData.settingsName = presetSetting.name;
        this.labelPrintingData.barcodeNeeded = presetSetting.barcodeSetting.barcodeNeeded;
        this.labelPrintingData.barcodeGeneratedAutomatically = presetSetting.barcodeSetting.automaticBarcode;
        this.labelPrintingData.includeHeadings = presetSetting.includeHeadings;

        if (this.labelPrintingData.barcodeNeeded && !this.labelPrintingData.barcodeGeneratedAutomatically) {
            this.labelPrintingData.firstBarcodeField = '';
            this.labelPrintingData.secondBarcodeField = '';
            this.labelPrintingData.thirdBarcodeField = '';

            if (presetSetting.barcodeSetting.barcodeFields[0]) {
                this.labelPrintingData.firstBarcodeField = presetSetting.barcodeSetting.barcodeFields[0];
            }
            if (presetSetting.barcodeSetting.barcodeFields[1]) {
                this.labelPrintingData.secondBarcodeField = presetSetting.barcodeSetting.barcodeFields[1];
            }
            if (presetSetting.barcodeSetting.barcodeFields[2]) {
                this.labelPrintingData.thirdBarcodeField = presetSetting.barcodeSetting.barcodeFields[2];
            }

        }
    }

    getSelectedLabelsHeader(title: string) {
        return 'label-printing.' + title.trim().replace(/ +/g, '.').toLowerCase();
    }

    deleteSelectedSetting() {
        const presetSetting = this.presetSettings.filter((preset) => preset.id === Number(this.presetSettingId))[0];
        this.modalTitle = 'Delete preset?';
        this.modalMessage = 'Are you sure you want to delete ' + presetSetting.name + ' ?';
        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        confirmModalRef.componentInstance.title = this.modalTitle;
        confirmModalRef.componentInstance.message = this.modalMessage;
        confirmModalRef.result.then(() => {
            this.service.deletePreset(this.presetSettingId).subscribe(() => {
                this.alertService.success('label-printing.delete.preset.success');
                this.labelPrintingData.settingsName = '';
                this.loadPresets();
            });
            this.activeModal.close();
        }, () => this.activeModal.dismiss());
        return;
    }

    private loadPresets() {
        return this.service.getAllPresets(this.getToolSection()).subscribe((PresetSettings) => {
            this.presetSettings = PresetSettings;
            this.presetSettingId = 0;
        });
    }

    reset() {
        if ((this.selectedfileType !== this.fileType && //
                (this.selectedfileType === FileType.PDF || this.selectedfileType === FileType.NONE) && //
                (this.fileType === FileType.EXCEL || this.fileType === FileType.CSV))
            || this.fileType === FileType.NONE || this.fileType === FileType.PDF) {
            this.reloadLabelTypeFields();

            this.labelPrintingData.barcodeNeeded = false;
            this.labelPrintingData.includeHeadings = true;
            this.sortBySelected = '';
            this.fieldsSelected = new Array();

            if (this.fileType === FileType.PDF) {
                this.fieldsSelected.push(new LabelType(LEFT_SIDE_FIELDS, null, []), new LabelType(RIGHT_SIDE_FIELDS, null, []));
            } else {
                this.fieldsSelected.push(new LabelType(SELECTED_FIELDS, null, []));

            }
        }
        this.selectedfileType = this.fileType;
    }

    export() {
        const fieldsSelected = [];
        const barcodeFieldsSelected = [];

        fieldsSelected.push(this.fieldsSelected[0].fields.map((field) => this.transformToCombinedKey(field)));
        if (this.fileType === FileType.PDF) {
            fieldsSelected.push(this.fieldsSelected[1].fields.map((field) => this.transformToCombinedKey(field)));
        }

        if (this.labelPrintingData.barcodeNeeded && !this.labelPrintingData.barcodeGeneratedAutomatically) {
            if (this.labelPrintingData.firstBarcodeField !== '') {
                barcodeFieldsSelected.push(this.labelPrintingData.firstBarcodeField);
            }
            if (this.labelPrintingData.secondBarcodeField !== '') {
                barcodeFieldsSelected.push(this.labelPrintingData.secondBarcodeField);
            }
            if (this.labelPrintingData.thirdBarcodeField !== '') {
                barcodeFieldsSelected.push(this.labelPrintingData.thirdBarcodeField);
            }

        }

        const labelsGeneratorInput = {
            datasetId: this.context.datasetId,
            studyId: this.context.studyId,
            selectedGIDs: this.context.selectedGIDs,
            isBrowseAllGermplasmResults: this.context.isBrowseAllGermplasmResults,
            selectedLotUUIDs: this.context.selectedLotUUIDs,
            searchRequestId: this.context.searchRequestId,
            listId: this.context.listId,
            sampleListId: this.context.sampleListId,
            fields: fieldsSelected,
            barcodeRequired: this.labelPrintingData.barcodeNeeded,
            automaticBarcode: this.labelPrintingData.barcodeGeneratedAutomatically,
            barcodeFields: barcodeFieldsSelected,
            sizeOfLabelSheet: this.labelPrintingData.sizeOfLabelSheet,
            numberOfRowsPerPageOfLabel: this.labelPrintingData.numberOfRowsPerPage,
            includeHeadings: this.labelPrintingData.includeHeadings,
            fileName: this.labelPrintingData.filename,
            sortBy: !this.sortBySelected ? undefined : this.sortBySelected
        };

        this.proceed = function downloadPrintingLabel(): void {
            this.isLoading = true;
            this.service.download(this.fileType, labelsGeneratorInput).pipe(finalize(() => {
                this.isLoading = false;
            })).subscribe((response: any) => {
                const fileName = this.fileDownloadHelper.getFileNameFromResponseContentDisposition(response);
                this.fileDownloadHelper.save(response.body, fileName);

            });
        };

        if (Number(this.presetSettingId) === 0) {
            this.modalTitle = this.translateService.instant('label-printing.preset.confirmation.title');
            this.modalMessage = this.translateService.instant('label-printing.preset.confirmation.proceed.export');
            const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
            confirmModalRef.componentInstance.title = this.modalTitle;
            confirmModalRef.componentInstance.message = this.modalMessage;
            confirmModalRef.result.then(() => {
                this.proceed();
                this.activeModal.close();
            }, () => this.activeModal.dismiss());
        } else {
            this.proceed();
        }
    }

    savePresets() {
        const preset: PresetSetting = new PresetSetting();
        const fileConfiguration: FileConfiguration = new FileConfiguration();
        const barcodeSetting: BarcodeSetting = new BarcodeSetting();
        const selectedFields = [];

        fileConfiguration.outputType = this.fileType.toString();

        selectedFields.push(this.fieldsSelected[0].fields.map((field) => this.transformToCombinedKey(field)));
        if (this.fileType === FileType.PDF) {
            selectedFields.push(this.fieldsSelected[1].fields.map((field) => this.transformToCombinedKey(field)));
            fileConfiguration.sizeOfLabelSheet = this.labelPrintingData.sizeOfLabelSheet;
            fileConfiguration.numberOfRowsPerPage = this.labelPrintingData.numberOfRowsPerPage;
        }

        barcodeSetting.barcodeNeeded = this.labelPrintingData.barcodeNeeded;
        barcodeSetting.automaticBarcode = barcodeSetting.barcodeNeeded ? this.labelPrintingData.barcodeGeneratedAutomatically : false;

        if (this.labelPrintingData.barcodeNeeded && !this.labelPrintingData.barcodeGeneratedAutomatically) {
            barcodeSetting.barcodeFields = new Array();
            if (this.labelPrintingData.firstBarcodeField !== '') {
                barcodeSetting.barcodeFields.push(this.labelPrintingData.firstBarcodeField);
            }
            if (this.labelPrintingData.secondBarcodeField !== '') {
                barcodeSetting.barcodeFields.push(this.labelPrintingData.secondBarcodeField);
            }
            if (this.labelPrintingData.thirdBarcodeField !== '') {
                barcodeSetting.barcodeFields.push(this.labelPrintingData.thirdBarcodeField);
            }

        }

        preset.programUUID = this.paramContext.programUUID;
        preset.toolSection = this.getToolSection();
        preset.toolId = 23;
        preset.name = this.labelPrintingData.settingsName;
        preset.type = 'LabelPrintingPreset';
        preset.selectedFields = selectedFields;
        preset.barcodeSetting = barcodeSetting;
        preset.fileConfiguration = fileConfiguration;
        preset.includeHeadings = this.labelPrintingData.includeHeadings;
        if (this.sortBySelected) {
            preset.sortBy = this.sortBySelected;
        }

        const presetSetting = this.presetSettings.filter((x) => x.name === preset.name)[0];

        if (!presetSetting) {
            this.isPresetSaving = true;
            this.service.addPreset(preset).subscribe((presetCreated) => {
                this.presetSettings.push(presetCreated);
                this.presetSettingId = presetCreated.id;
                this.alertService.success('label-printing.save.preset.success');
                this.isPresetSaving = false;
            });
        } else {
            this.modalTitle = this.translateService.instant('label-printing.preset.confirmation.title');
            this.modalMessage = this.translateService.instant('label-printing.preset.confirmation.message', { param: presetSetting.name });
            const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
            confirmModalRef.componentInstance.title = this.modalTitle;
            confirmModalRef.componentInstance.message = this.modalMessage;
            confirmModalRef.result.then(() => {
                this.presetSettingId = presetSetting.id;
                preset.id = presetSetting.id;
                this.service.updatePreset(preset).subscribe((res: void) => {
                    presetSetting.selectedFields = preset.selectedFields;
                    presetSetting.barcodeSetting = preset.barcodeSetting;
                    presetSetting.fileConfiguration = preset.fileConfiguration;
                    presetSetting.includeHeadings = preset.includeHeadings;
                    presetSetting.sortBy = preset.sortBy;
                    this.alertService.success('label-printing.update.preset.success');
                });
                this.activeModal.close();
            }, () => this.activeModal.dismiss());
        }

    }

    private getToolSection() {
        switch (this.context.printingLabelType) {
        case LabelPrintingType.OBSERVATION_DATASET:
        case LabelPrintingType.SUBOBSERVATION_DATASET:
            return 'DATASET_LABEL_PRINTING_PRESET';
        case LabelPrintingType.LOT:
            return 'LOT_LABEL_PRINTING_PRESET';
        case LabelPrintingType.GERMPLASM:
            return 'GERMPLASM_LABEL_PRINTING_PRESET';
        case LabelPrintingType.GERMPLASM_LIST:
            return 'GERMPLASM_LIST_LABEL_PRINTING_PRESET';
        case LabelPrintingType.STUDY_ENTRIES:
            return 'STUDY_ENTRIES_LABEL_PRINTING_PRESET';
        case LabelPrintingType.SAMPLE:
            return 'SAMPLE_LABEL_PRINTING_PRESET';
        default:
            return;
        }
    }

    getFileType(extension: string): FileType {
        switch (extension) {
        case FileType.CSV.toString():
            return FileType.CSV;
        case FileType.PDF.toString():
            return FileType.PDF;
        case FileType.EXCEL.toString():
            return FileType.EXCEL;
        default:
            return FileType.NONE;
        }
    }

    // TODO translateService
    getChooseLabelDescription() {
        if (!this.labelTypes || !this.labelTypes.length) {
            return;
        }

        const from = this.labelTypes.map((l) => `<strong>${l.title}</strong>`).join(', ');
        let description = this.translateService.instant('label-printing.drag.fields') + from;

        if (this.fileType === FileType.PDF) {
            description += this.translateService.instant('label-printing.for.pdf', { param: MAX_LABELS_PER_SIDE_FOR_PDF_FORMAT });
        } else {
            description += this.translateService.instant('label-printing.into.the.selected');
        }

        return description;
    }

    back() {

        if (this.context.openedDirectlyFromNewStudyEditor) {
            this.studyEditorService.navigateToTab(this.context.studyId, 'study-settings');
        } else {
            const program = new Program();
            program.crop = this.paramContext.cropName;
            program.id = String(this.paramContext.selectedProjectId);
            program.uniqueID = this.paramContext.programUUID;
            if (this.context.printingLabelType === LabelPrintingType.SUBOBSERVATION_DATASET
                || this.context.printingLabelType === LabelPrintingType.OBSERVATION_DATASET
                || this.context.printingLabelType === LabelPrintingType.STUDY_ENTRIES
                || this.context.printingLabelType === LabelPrintingType.SAMPLE) {
                this.urlService.openStudy(this.context.studyId, this.context.studyName, program);
            } else if (this.context.printingLabelType === LabelPrintingType.LOT) {
                this.router.navigate(['', ViewEnum.INVENTORY_MANAGER], { queryParamsHandling: 'preserve' });
            } else {
                window.history.back();
            }
        }
    }

    resetSelectedFieldsTable($event, list?: LabelType) {
        $event.preventDefault();
        const fieldsSelected: number[][] = [];
        if (list.fields.length > 0) {
            if (this.fileType !== FileType.PDF.toString()) {
                this.reloadLabelTypeFields();
            } else {
                list.fields.forEach((selectedField) => {
                    this.labelTypes.forEach((label: LabelType) => {
                        const fields = this.labelTypesOrigMap[label.title];
                        const index = fields.findIndex((field) => field.id === selectedField.id);
                        if (index > -1) {
                            label.fields.splice(index, 0, selectedField);
                            label.fields = label.fields.map((x) => Object.assign({}, x));
                        }
                    });
                });
            }
            list.fields = [];
        }
    }

    reloadLabelTypeFields() {
        this.labelTypes.forEach((label: LabelType) => {
            label.fields = this.labelTypesOrigMap[label.title].map((x) => Object.assign({}, x));
        });
    }

    removeItem(listSelected: LabelType, removedField: any) {
        this.labelTypes.forEach((label: LabelType) => {
            const fields = this.labelTypesOrigMap[label.title];
            const idx = fields.findIndex((field) => field.id === removedField.id);
            if (idx > -1) {
                label.fields.splice(idx, 0, removedField);
                label.fields = label.fields.map((x) => Object.assign({}, x));
            }
        });
        const index = listSelected.fields.findIndex((field) => field.id === removedField.id);
        listSelected.fields.splice(index, 1);
        listSelected.fields = listSelected.fields.map((x) => Object.assign({}, x));
    }

    drop(event: CdkDragDrop<{ id: number, name: string }[]>) {
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        } else {
            if (SELECTED_FIELDS !== event.container.id && //
                LEFT_SIDE_FIELDS !== event.container.id && //
                RIGHT_SIDE_FIELDS !== event.container.id) {
                const fields = this.labelTypesOrigMap[event.container.id];
                const index = fields.findIndex((field) => field.id === event.item.data.id);
                // Restriction to avoid moving the element to a list that does not belong.
                if (index === -1) {
                    return;
                }
            } else {
                // Restritcion to limit the max number of labels per side for PDF output format.
                if (this.fileType === FileType.PDF && event.container.data.length === MAX_LABELS_PER_SIDE_FOR_PDF_FORMAT) {
                    this.alertService.warning('label-printing.max.labels.allowed.to.pdf.format', { param: MAX_LABELS_PER_SIDE_FOR_PDF_FORMAT });
                    return;
                }
            }

            const previousIndex = event.previousContainer.data.findIndex((x) => x.id === event.item.data.id);
            transferArrayItem(
                event.previousContainer.data,
                event.container.data,
                previousIndex,
                event.currentIndex,
            );

            // Workaround to reload the list of fieldListFilter when the filterText does not change.
            this.labelTypes.forEach((label: LabelType) => {
                label.fields = label.fields.map((x) => Object.assign({}, x));
            });
        }
    }

    transformToCombinedKey(field: any) {
        return field.fieldType + '_' + field.id;
    }

    getJsonKey(key: string) {
        return 'label-printing.metadata.' + key.trim().replace(/ +/g, '.').toLowerCase();
    }
}

@Pipe({
    name: 'fieldListFilter'
})
export class FieldListFilterPipe implements PipeTransform {
    transform(fields: any[], filterText: string): any {
        return !filterText
            ? fields
            : fields.filter(
                (item) =>
                    item.name.toLowerCase().includes(filterText.toLowerCase())
            );
    }
}

@Pipe({
    name: 'transformToCombinedKey'
})
export class TransformToCombinedKeyPipe implements PipeTransform {
    transform(field: any): any {
        return field.fieldType + '_' + field.id;
    }

}

